// src/pages/Admin/QuizReports.js
import React, { useEffect, useState } from 'react';
import adminApi from '../../services/adminApi';
import styles from './QuizReports.module.css';

export default function QuizReports() {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        reason: '',
        status: '',
        search: '',
        page: 1
    });
    const [pagination, setPagination] = useState(null);
    const [selectedReport, setSelectedReport] = useState(null);

    useEffect(() => {
        fetchReports();
    }, [filters]);

    const fetchReports = async () => {
        try {
            setLoading(true);
            const data = await adminApi.listQuizReports(filters);
            setReports(data.reports);
            setPagination(data.pagination);
        } catch (err) {
            console.error('Failed to load quiz reports:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (id, status) => {
        try {
            await adminApi.updateQuizReportStatus(id, status);
            fetchReports();
            if (selectedReport?.id === id) {
                setSelectedReport(prev => ({ ...prev, status }));
            }
        } catch (err) {
            console.error('Failed to update status:', err);
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'new': return styles.statusNew;
            case 'reviewed': return styles.statusReviewed;
            case 'fixed': return styles.statusFixed;
            case 'dismissed': return styles.statusDismissed;
            default: return '';
        }
    };

    const getReasonBadgeClass = (reason) => {
        switch (reason) {
            case 'wrong_answer_marked_correct': return styles.reasonWrong;
            case 'correct_answer_missing': return styles.reasonMissing;
            case 'question_confusing': return styles.reasonConfusing;
            case 'spoiler_issue': return styles.reasonSpoiler;
            case 'wrong_media': return styles.reasonMedia;
            case 'typo_wording': return styles.reasonTypo;
            case 'multiple_correct_answers': return styles.reasonMultiple;
            case 'factual_error': return styles.reasonFactual;
            default: return '';
        }
    };

    return (
        <div className={styles.quizReports}>
            <div className={styles.header}>
                <h1 className={styles.pageTitle}>Quiz Question Reports</h1>
            </div>

            <div className={styles.filters}>
                <input
                    type="text"
                    placeholder="Search..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                    className={styles.searchInput}
                />

                <select
                    value={filters.reason}
                    onChange={(e) => setFilters(prev => ({ ...prev, reason: e.target.value, page: 1 }))}
                    className={styles.select}
                >
                    <option value="">All Reasons</option>
                    <option value="wrong_answer_marked_correct">Wrong Answer Marked Correct</option>
                    <option value="correct_answer_missing">Correct Answer Missing</option>
                    <option value="question_confusing">Question Confusing</option>
                    <option value="spoiler_issue">Spoiler Issue</option>
                    <option value="wrong_media">Wrong Media</option>
                    <option value="typo_wording">Typo/Wording Issue</option>
                    <option value="multiple_correct_answers">More Than One Answer Correct</option>
                    <option value="factual_error">Factual Error</option>
                    <option value="other">Other</option>
                </select>

                <select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                    className={styles.select}
                >
                    <option value="">All Statuses</option>
                    <option value="new">New</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="fixed">Fixed</option>
                    <option value="dismissed">Dismissed</option>
                </select>
            </div>

            {loading ? (
                <div className={styles.loading}>Loading reports...</div>
            ) : reports.length === 0 ? (
                <div className={styles.empty}>No quiz reports found</div>
            ) : (
                <>
                    <div className={styles.table}>
                        <table>
                            <thead>
                            <tr>
                                <th>Title</th>
                                <th>Question</th>
                                <th>Reason</th>
                                <th>Reporter</th>
                                <th>Status</th>
                                <th>Date</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {reports.map(report => (
                                <tr key={report.id}>
                                    <td>
                                        <div className={styles.titleCell}>
                                            {report.title || '—'}
                                            {report.season_number != null && (
                                                <div className={styles.detailRow}>
                                                    <label>Season:</label>
                                                    <span>Season {report.season_number}</span>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.questionPreview}>
                                            {report.question_text?.substring(0, 80) || '—'}
                                            {report.question_text?.length > 80 && '...'}
                                        </div>
                                    </td>
                                    <td>
                                            <span className={`${styles.reasonBadge} ${getReasonBadgeClass(report.reason)}`}>
                                                {report.reason.replace(/_/g, ' ')}
                                            </span>
                                    </td>
                                    <td>
                                        <div className={styles.userInfo}>
                                            <div className={styles.username}>
                                                {report.reporter?.username || report.username || 'Logged-out user'}
                                            </div>
                                            <div className={styles.email}>
                                                {report.reporter?.email || report.email || '—'}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <select
                                            value={report.status}
                                            onChange={(e) => handleStatusChange(report.id, e.target.value)}
                                            className={`${styles.statusSelect} ${getStatusBadgeClass(report.status)}`}
                                        >
                                            <option value="new">New</option>
                                            <option value="reviewed">Reviewed</option>
                                            <option value="fixed">Fixed</option>
                                            <option value="dismissed">Dismissed</option>
                                        </select>
                                    </td>
                                    <td className={styles.date}>
                                        {report.createdAt || report.created_at ? new Date(report.createdAt || report.created_at).toLocaleDateString() : '—'}
                                    </td>
                                    <td>
                                        <button
                                            onClick={() => setSelectedReport(report)}
                                            className={styles.viewBtn}
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {pagination && pagination.totalPages > 1 && (
                        <div className={styles.pagination}>
                            <button
                                disabled={pagination.page === 1}
                                onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                                className={styles.pageBtn}
                            >
                                Previous
                            </button>
                            <span className={styles.pageInfo}>
                                Page {pagination.page} of {pagination.totalPages}
                            </span>
                            <button
                                disabled={pagination.page === pagination.totalPages}
                                onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                                className={styles.pageBtn}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {selectedReport && (
                <ReportDetailModal
                    report={selectedReport}
                    onClose={() => setSelectedReport(null)}
                    onStatusChange={handleStatusChange}
                />
            )}
        </div>
    );
}

const SOURCE_LABELS = {
    missed_review: 'Review Missed Questions',
    quiz_result: 'Quiz result',
};

function ReportDetailModal({ report: listRow, onClose, onStatusChange }) {
    const [detail, setDetail] = useState(null);

    useEffect(() => {
        let cancelled = false;
        adminApi.getQuizReport(listRow.id)
            .then((data) => { if (!cancelled) setDetail(data); })
            .catch((err) => console.error('Failed to load quiz report detail:', err));
        return () => { cancelled = true; };
    }, [listRow.id]);

    // Status edits update the list row; everything else comes from the detail.
    const report = { ...(detail || {}), ...listRow };
    const attempt = detail?.quizAttempt;
    const currentQuestion = detail?.question;
    const currentOptions = Array.isArray(currentQuestion?.options_json) ? currentQuestion.options_json : null;
    const currentCorrect = currentOptions ? currentOptions[currentQuestion.correct_index] : null;
    const source = report.metadata?.source;

    return (
        <div className={styles.modal} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>Quiz Question Report Detail</h2>
                    <button onClick={onClose} className={styles.closeBtn}>×</button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.detailRow}>
                        <label>Title:</label>
                        <span>{report.title || '—'}</span>
                    </div>

                    {report.media_type && (
                        <div className={styles.detailRow}>
                            <label>Media Type:</label>
                            <span>{report.media_type === 'tv' ? 'TV' : 'Movie'}{report.tmdb_movie_id || report.tmdb_show_id ? ` · TMDB ${report.tmdb_movie_id || report.tmdb_show_id}` : ''}</span>
                        </div>
                    )}

                    {report.season_number != null && (
                        <div className={styles.detailRow}>
                            <label>Season:</label>
                            <span>Season {report.season_number}</span>
                        </div>
                    )}

                    {source && (
                        <div className={styles.detailRow}>
                            <label>Reported From:</label>
                            <span>{SOURCE_LABELS[source] || source}</span>
                        </div>
                    )}

                    {report.quiz_attempt_id && (
                        <div className={styles.detailRow}>
                            <label>Quiz Attempt:</label>
                            <span>
                                {report.quiz_attempt_id}
                                {attempt && ` · ${attempt.score ?? '?'}/${attempt.total_questions ?? 8}${attempt.result_tier ? ` · ${attempt.result_tier}` : ''}`}
                            </span>
                        </div>
                    )}

                    <div className={styles.detailRow}>
                        <label>Question:</label>
                        <p className={styles.questionText}>{report.question_text || '—'}</p>
                    </div>

                    <div className={styles.detailRow}>
                        <label>Selected Answer:</label>
                        <span>{report.selected_answer || '—'}</span>
                    </div>

                    <div className={styles.detailRow}>
                        <label>Configured Correct Answer:</label>
                        <span>{report.correct_answer || '—'}</span>
                    </div>

                    {currentCorrect != null && currentCorrect !== report.correct_answer && (
                        <div className={styles.detailRow}>
                            <label>Current Correct Answer:</label>
                            <span>{currentCorrect} (question edited since report)</span>
                        </div>
                    )}

                    {detail && !currentQuestion && report.question_id && (
                        <div className={styles.detailRow}>
                            <label>Question Record:</label>
                            <span>Deleted since report</span>
                        </div>
                    )}

                    <div className={styles.detailRow}>
                        <label>Reason:</label>
                        <span>{report.reason.replace(/_/g, ' ')}</span>
                    </div>

                    <div className={styles.detailRow}>
                        <label>User Message:</label>
                        <p className={styles.messageText}>{report.message || '—'}</p>
                    </div>

                    <div className={styles.detailRow}>
                        <label>Reporter:</label>
                        <span>{report.reporter?.username || report.username || 'Logged-out user'}</span>
                    </div>

                    <div className={styles.detailRow}>
                        <label>Reporter Email:</label>
                        <span>{report.reporter?.email || report.email || '—'}</span>
                    </div>

                    <div className={styles.detailRow}>
                        <label>Submitted:</label>
                        <span>{report.createdAt || report.created_at ? new Date(report.createdAt || report.created_at).toLocaleString() : '—'}</span>
                    </div>

                    <div className={styles.detailRow}>
                        <label>Status:</label>
                        <select
                            value={report.status}
                            onChange={(e) => onStatusChange(report.id, e.target.value)}
                            className={styles.statusSelect}
                        >
                            <option value="new">New</option>
                            <option value="reviewed">Reviewed</option>
                            <option value="fixed">Fixed</option>
                            <option value="dismissed">Dismissed</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
}